// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@adl/App/Components/Common/XngZone";
import { xSafariOnly, xScreen } from "@adl/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

const enum fields {
    showHeaderActionSection = "showHeaderActionSection",
    showBillingAndShippingInfo = "showBillingAndShippingInfo",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showHeaderActionSection]: boolean;
        [fields.showBillingAndShippingInfo]: boolean;
    };
}

export interface MyAccountDetailsPagesContainerStyles {
    mainWrapper?: InjectableCss;
    headerContainer?: GridContainerProps;
    headerGridItem?: GridItemProps;
    contentContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    lineListingGridItem?: GridItemProps;
    titleGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    content?: InjectableCss;
    informationWrapper?: InjectableCss;
    shippingBillingWrapper?: InjectableCss;
}

export const myAccountDetailsPagesContainerStyles: MyAccountDetailsPagesContainerStyles = {
    headerContainer: {
        gap: 10,
    },
    headerGridItem: {
        width: 12,
        printWidth: 12,
    },
    contentContainer: {
        gap: 40,
        offsetCss: css`
            @media print {
                display: block;
                width: 100%;
                &:after {
                    content: "";
                    clear: both;
                    display: table;
                }
            }
        `,
    },
    leftColumnGridItem: {
        width: [12, 12, 12, 9, 9],
        printWidth: 12,
        css: css`
            flex-direction: column;
        `,
    },
    rightColumnGridItem: {
        width: [12, 12, 12, 3, 3],
        printWidth: 12,
        css: css`
            @media print {
                padding: 5px;
                display: block;
            }
        `,
    },
    lineListingGridItem: { width: 12 },
    titleGridItem: { width: [10, 10, 8, 5, 5] },
    actionsGridItem: { width: [2, 2, 4, 7, 7] },
    content: {
        css: css`
            width: 100%;
            & > div {
                @media print {
                    float: left;
                }
            }
        `,
    },
    informationWrapper: {
        css: css`
            width: 100%;
            ${xScreen("lte", "sm")} {
                margin-bottom: 10px;
            }
            @media print {
                margin-bottom: 0px;
            }
            & > div[class^="Wrapper"] {
                display: flex;
                flex-wrap: wrap;
                @media print {
                    display: block;
                    max-width: 100%;
                    > div > h2,
                    > div > h3 {
                        display: inline;
                    }
                    &:after {
                        display: block;
                        content: "";
                        clear: both;
                    }
                }
                > div {
                    width: 25%;
                    margin-bottom: 22px;
                    ${xScreen("lte", "sm")} {
                        width: 50%;
                    }
                    ${xScreen("lte", "xs")} {
                        width: 100%;
                    }
                    &:last-child {
                        margin-bottom: 0;
                    }
                    @media print {
                        display: block;
                        margin-bottom: 0;
                        float: left;
                        width: 50%;
                    }
                }
                &:empty {
                    display: none;
                }
                ${xScreen(300, 2800)} {
                    @media print {
                        &:empty {
                            display: none;
                        }
                    }
                    ${xSafariOnly(`@media print {
                         width: 100%;
                    }`)}
                }
            }
        `,
    },
    shippingBillingWrapper: {
        css: css`
            width: 100%;
            display: flex;
            flex-wrap: wrap;
            margin-top: 16px;
            @media print {
                display: block;
                margin-top: 0;
            }
            & > div[class^="Wrapper"] {
                display: flex;
                flex-direction: column;
                &:empty {
                    display: none;
                }
                width: 50%;
                ${xScreen("lte", "xs")} {
                    width: 100%;
                }
                @media print {
                    display: block;
                    float: left;
                    width: 100%;
                }
                &:first-child {
                    padding-right: 45px;
                    ${xScreen("lte", "xs")} {
                        padding-right: 0;
                        margin-bottom: 20px;
                    }
                }
            }
        `,
    },
};

const styles = myAccountDetailsPagesContainerStyles;

type Props = OwnProps;

const MyAccountDetailsPagesContainer = ({
    id,
    fields: { showHeaderActionSection, showBillingAndShippingInfo },
}: Props) => {
    return (
        <StyledWrapper {...styles.mainWrapper}>
            <GridContainer {...styles.headerContainer}>
                {!showHeaderActionSection ? (
                    <GridItem {...styles.headerGridItem}>
                        <XngZone contentId={id} zoneName="Content00" hint="Zone For Page Header" />
                    </GridItem>
                ) : (
                    <>
                        <GridItem {...styles.titleGridItem}>
                            <XngZone contentId={id} zoneName="Content00" hint="Zone For Page Title" />
                        </GridItem>
                        <GridItem {...styles.actionsGridItem}>
                            <XngZone contentId={id} zoneName="Content01" hint="Zone For Header and Action Buttons" />
                        </GridItem>
                    </>
                )}
            </GridContainer>
            <GridContainer {...styles.contentContainer}>
                <GridItem {...styles.leftColumnGridItem}>
                    <StyledWrapper {...styles.content}>
                        <StyledWrapper {...styles.informationWrapper} {...{ showBillingAndShippingInfo }}>
                            <XngZone contentId={id} zoneName="Content02" hint="Zone For Details Information" />
                        </StyledWrapper>
                        {showBillingAndShippingInfo ? (
                            <StyledWrapper {...styles.shippingBillingWrapper}>
                                <XngZone
                                    contentId={id}
                                    zoneName="Content03"
                                    hint="Zone For Details Shipping Information"
                                />
                                <XngZone
                                    contentId={id}
                                    zoneName="Content04"
                                    hint="Zone For Details Billing Information"
                                />
                            </StyledWrapper>
                        ) : null}

                        <XngZone contentId={id} zoneName="Content05" hint="Zone For Details Extra Information" />
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.rightColumnGridItem}>
                    <XngZone contentId={id} zoneName="Content06" hint="Zone For Order Summary" />
                </GridItem>
            </GridContainer>
            <XngZone contentId={id} zoneName="Content07" hint="Zone For Line Listing" />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: MyAccountDetailsPagesContainer,
    definition: {
        group: "Xngage Custom" as any,
        displayName: "My Account Details Pages Container",
        fieldDefinitions: [
            {
                name: fields.showHeaderActionSection,
                displayName: "Show header action section",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showBillingAndShippingInfo,
                displayName: "Show billing and shipping information seperately",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
